import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge, Box, Button, ClickAwayListener, IconButton, InputAdornment, List, ListItemButton, ListItemText, Paper, Popover, Popper, Stack, TextField, Typography } from "@mui/material";
import { Bell, Search } from "lucide-react";
import { hd } from "./api";
import { useSocketEvent } from "./socket";
import { T, TYPE, formatRelative } from "../user/complaints/shared";

const LINKS = [
  ["/helpdesk/tickets", "Tickets"], ["/helpdesk/chat", "Live Chat"], ["/helpdesk/kb", "Knowledge Base"],
  ["/helpdesk/dashboard", "Dashboard"], ["/helpdesk/reports", "Reports"], ["/helpdesk/settings", "Settings"],
];

export default function TopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const anchor = useRef(null);
  const [bellEl, setBellEl] = useState(null);
  const [notes, setNotes] = useState({ rows: [], unread: 0 });

  const loadNotes = () => hd.notifications().then(setNotes).catch(() => {});
  useEffect(() => {
    loadNotes();
  }, []);
  useSocketEvent("notification.created", (n) => {
    setNotes((s) => ({ rows: [n, ...s.rows].slice(0, 50), unread: s.unread + 1 }));
    if (document.hidden && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification(n.title, { body: n.body }); // eslint-disable-line no-new
      } catch {
        /* ignore */
      }
    }
  });

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return undefined;
    }
    const t = setTimeout(() => hd.search(q.trim()).then(setResults).catch(() => setResults(null)), 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (url) => {
    setQ("");
    setResults(null);
    navigate(url);
  };

  const openNote = async (n) => {
    setBellEl(null);
    if (!n.readAt) {
      hd.readNotifications([n._id]).catch(() => {});
      setNotes((s) => ({ rows: s.rows.map((x) => (x._id === n._id ? { ...x, readAt: new Date() } : x)), unread: Math.max(0, s.unread - 1) }));
    }
    if (n.link) navigate(n.link);
  };

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }} useFlexGap>
        {LINKS.map(([to, label]) => (
          <Button key={to} size="small" onClick={() => navigate(to)}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", color: pathname.startsWith(to) ? T.brand : T.inkSecondary, bgcolor: pathname.startsWith(to) ? T.brandSoft : "transparent" }}>
            {label}
          </Button>
        ))}
      </Stack>
      <Box sx={{ flexGrow: 1 }} />
      <ClickAwayListener onClickAway={() => setResults(null)}>
        <Box ref={anchor} sx={{ position: "relative", width: { xs: "100%", md: 360 } }}>
          <TextField size="small" fullWidth placeholder="Search tickets, messages, customers…" value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }} />
          <Popper open={Boolean(results)} anchorEl={anchor.current} placement="bottom-end" sx={{ zIndex: 1400, width: anchor.current?.offsetWidth }}>
            <Paper sx={{ mt: 0.5, maxHeight: 420, overflowY: "auto", border: `1px solid ${T.border}` }}>
              {results && !results.tickets.length && !results.contacts.length && !results.messages.length && <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>No matches</Typography>}
              {results?.tickets.length > 0 && <Typography sx={{ px: 2, pt: 1, fontSize: TYPE.micro, fontWeight: 700, color: T.inkMuted }}>TICKETS</Typography>}
              <List dense>
                {results?.tickets.map((t) => <ListItemButton key={t._id} onClick={() => go(`/helpdesk/tickets/${t._id}`)}><ListItemText primary={`${t.key} · ${t.subject}`} secondary={t.requester?.email} /></ListItemButton>)}
              </List>
              {results?.contacts.length > 0 && <Typography sx={{ px: 2, fontSize: TYPE.micro, fontWeight: 700, color: T.inkMuted }}>CUSTOMERS</Typography>}
              <List dense>
                {results?.contacts.map((c) => <ListItemButton key={c._id} onClick={() => go(`/helpdesk/customers/${c._id}`)}><ListItemText primary={`${c.firstName} ${c.lastName || ""}`} secondary={c.email || c.phone} /></ListItemButton>)}
              </List>
              {results?.messages.length > 0 && <Typography sx={{ px: 2, fontSize: TYPE.micro, fontWeight: 700, color: T.inkMuted }}>MESSAGES</Typography>}
              <List dense>
                {results?.messages.map((m) => <ListItemButton key={m._id} onClick={() => go(`/helpdesk/tickets/${m.ticketId}`)}><ListItemText primary={m.content} secondary={`${m.senderType.toLowerCase()} · ${formatRelative(m.createdAt)}`} primaryTypographyProps={{ noWrap: true }} /></ListItemButton>)}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
      <IconButton onClick={(e) => { setBellEl(e.currentTarget); if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {}); }} aria-label="Notifications">
        <Badge color="error" badgeContent={notes.unread} max={99}><Bell size={19} /></Badge>
      </IconButton>
      <Popover open={Boolean(bellEl)} anchorEl={bellEl} onClose={() => setBellEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box sx={{ width: 360, maxHeight: 460, overflowY: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.border}` }}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>Notifications</Typography>
            {notes.unread > 0 && <Button size="small" onClick={() => hd.readNotifications([]).then(loadNotes)}>Mark all read</Button>}
          </Stack>
          {!notes.rows.length && <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>You're all caught up</Typography>}
          <List dense disablePadding>
            {notes.rows.map((n) => (
              <ListItemButton key={n._id} onClick={() => openNote(n)} sx={{ bgcolor: n.readAt ? "transparent" : T.brandSoft, borderBottom: `1px solid ${T.border}` }}>
                <ListItemText primary={n.title} secondary={`${n.body ? `${n.body.slice(0, 80)} · ` : ""}${formatRelative(n.createdAt)}`} primaryTypographyProps={{ fontSize: TYPE.small, fontWeight: n.readAt ? 500 : 700 }} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Popover>
    </Stack>
  );
}
